/**
 * Board composition — pure functions that turn five sibling contracts into
 * the three lanes of spec #44 §1.2, and nothing else.
 *
 * These are separated from `WorkspaceProvider` for the same reason
 * `workspaceDerivations` is: composition is where attribution happens, and
 * attribution is where a UI is most tempted to guess. Every function here
 * returns `null` rather than inventing an owner, a number, or a state.
 */

import type { ActionProposal } from '../types/actionProposals';
import type { HandoffItem } from '../types/handoffs';
import { HUMAN_HANDOFF_TYPES } from '../types/handoffs';
import type { Specialist } from '../types/marketingSuite';
import type { Bet, PlanLane, PlanResponse } from '../types/plan';
import { isApprovedPlan } from '../types/plan';
import type { TimelineEntry, TimelineHead } from '../types/timeline';
import type { AgentSettings } from '../types/agentSettings';
import type { LiveWorkTile, WorkspaceQuestion } from '../types/workspace';

/** `{specialist_key: owner domain}`, built from the catalog at runtime. */
export type SpecialistDomains = Record<string, string>;

export function specialistDomains(
  specialists: readonly Specialist[]
): SpecialistDomains {
  const map: SpecialistDomains = {};
  specialists.forEach((s) => {
    map[s.key] = s.domain;
  });
  return map;
}

/**
 * Which agent owns this question (§1.2): `source_belief.domain` when the
 * proposal is belief-sourced, else `specialist_key → the catalog's domain`.
 *
 * Returns `null` when neither names an agent. That `null` is load-bearing:
 * the card renders "not attributed to an agent" rather than picking one.
 */
export function attributeQuestion(
  proposal: ActionProposal,
  specialists: SpecialistDomains
): string | null {
  if (proposal.source_belief?.domain) return proposal.source_belief.domain;
  const key = proposal.specialist_key;
  if (key && specialists[key]) return specialists[key];
  return null;
}

/** A proposal carrying a plan version wears the plan face, not the
 * budget-change face (§1.2 (c)). */
export function isPlanApproval(proposal: ActionProposal): boolean {
  return (
    proposal.plan_version !== undefined && proposal.plan_version !== null
  );
}

/** Normalise a proposal or a hand-off into one question row. */
export function toWorkspaceQuestion(
  item: ActionProposal | HandoffItem,
  specialists: SpecialistDomains = {}
): WorkspaceQuestion {
  if ('proposal_id' in item) {
    return {
      id: `${isPlanApproval(item) ? 'plan' : 'proposal'}:${item.proposal_id}`,
      source: isPlanApproval(item) ? 'plan_approval' : 'proposal',
      owner_domain: attributeQuestion(item, specialists),
      title: item.target.entity_name || item.expected_effect,
      occurred_at: item.created_at,
      proposal: item,
    };
  }
  return {
    id: `handoff:${item.id}`,
    source: 'handoff',
    // A hand-off's recipient is always an agent, by #46's own construction.
    owner_domain: item.to_domain,
    title: handoffTitle(item),
    occurred_at: item.created_at,
    handoff: item,
  };
}

/** A hand-off's one-line title, from its typed payload only. */
export function handoffTitle(item: HandoffItem): string {
  const note = item.payload?.note;
  if (typeof note === 'string' && note.trim()) return note.trim();
  const tool = item.payload?.tool_key;
  if (typeof tool === 'string' && tool.trim()) {
    return `${humanHandoffLabel(item.type)} · ${tool}`;
  }
  return humanHandoffLabel(item.type);
}

export function humanHandoffLabel(type: HandoffItem['type']): string {
  switch (type) {
    case 'tool_request':
      return 'Tool request';
    case 'review_request':
      return 'Review request';
    case 'finding_for_owner':
      return 'Finding for owner';
    case 'belief_challenge':
      return 'Belief challenge';
    case 'prerequisite_output':
      return 'Prerequisite output';
    default:
      return type;
  }
}

/**
 * The questions lane: open tier-2 proposals (including plan approvals) and
 * the two HUMAN hand-off types, newest first. The three cycle-consumed
 * hand-off types are excluded — they are the engine's input, and no person
 * is waiting on them.
 */
export function composeQuestions(
  proposals: readonly ActionProposal[],
  handoffs: readonly HandoffItem[],
  specialists: SpecialistDomains
): WorkspaceQuestion[] {
  const fromProposals = proposals
    .filter((p) => p.status === 'proposed')
    .map((p) => toWorkspaceQuestion(p, specialists));
  const fromHandoffs = handoffs
    .filter(
      (h) => HUMAN_HANDOFF_TYPES.includes(h.type) && h.status === 'open'
    )
    .map((h) => toWorkspaceQuestion(h, specialists));
  return [...fromProposals, ...fromHandoffs].sort(
    (a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)
  );
}

/** This agent's lane, via delta 3's `owner_domain`. `null` when the plan
 * has none for it — never the first lane as a stand-in. */
export function planLaneForDomain(
  plan: PlanResponse | null,
  domain: string
): PlanLane | null {
  if (!plan || !isApprovedPlan(plan)) return null;
  return plan.lanes.find((lane) => lane.owner_domain === domain) ?? null;
}

/** Bets grouped by `commitment.owner_domain` (§1.2 lane 3). */
export function betsByDomain(
  bets: readonly Bet[]
): Record<string, Bet[]> {
  const grouped: Record<string, Bet[]> = {};
  bets.forEach((bet) => {
    const domain = bet.commitment.owner_domain;
    (grouped[domain] ||= []).push(bet);
  });
  return grouped;
}

/**
 * The live-work lane (§1.2 lane 2). `head.active_agents` gives the live
 * tiles; each tile's one-line "now" is that agent's newest timeline title,
 * or `null` when nothing has been read yet — never an invented status.
 */
export function liveAgentTiles(
  agents: readonly AgentSettings[],
  head: TimelineHead | null,
  newestByDomain: Record<string, TimelineEntry | undefined>
): LiveWorkTile[] {
  const active = new Set(head?.active_agents ?? []);
  return agents
    .filter((a) => active.has(a.domain))
    .map((a) => ({
      key: `agent:${a.domain}`,
      label: a.display_name,
      domain: a.domain,
      detail: newestByDomain[a.domain]?.title ?? null,
      kind: 'agent' as const,
      status: newestByDomain[a.domain]?.status ?? null,
    }));
}

/** Specialist tiles come from `last_run_status` — the only run signal the
 * catalog carries. Specialists with no run yet are not tiles. */
export function specialistTiles(
  specialists: readonly Specialist[]
): LiveWorkTile[] {
  return specialists
    .filter((s) => s.enabled && s.last_run_status === 'running')
    .map((s) => ({
      key: `specialist:${s.key}`,
      label: s.name,
      domain: s.domain,
      detail: s.mission,
      kind: 'specialist' as const,
      status: s.last_run_status,
    }));
}

/** Everyone enabled who is not on a live tile — the "Quiet right now" row. */
export function quietAgents(
  agents: readonly AgentSettings[],
  head: TimelineHead | null
): AgentSettings[] {
  const active = new Set(head?.active_agents ?? []);
  return agents.filter(
    (a) => a.configured && a.enabled && !active.has(a.domain)
  );
}

/**
 * Agents in `SEED_AGENTS` order, then anything the server returns that the
 * seed list does not name (a new agent must never vanish from the sidebar
 * because this constant is out of date).
 */
export const SEED_AGENT_ORDER: string[] = [
  'product_marketing',
  'paid_media',
  'lifecycle',
  'revops',
];

export function inSeedOrder(
  agents: readonly AgentSettings[]
): AgentSettings[] {
  const rank = (domain: string) => {
    const index = SEED_AGENT_ORDER.indexOf(domain);
    return index === -1 ? SEED_AGENT_ORDER.length : index;
  };
  return [...agents].sort((a, b) => {
    const delta = rank(a.domain) - rank(b.domain);
    return delta !== 0 ? delta : a.domain.localeCompare(b.domain);
  });
}
